import React, { useState } from 'react';

const ResumeBuilder = () => {
  const [formData, setFormData] = useState({
    fullName: 'JOHN DOE',
    email: 'john.doe@example.com',
    phone: '123-456-7890',
    linkedin: 'linkedin.com/in/johndoe',
    github: 'github.com/johndoe',
    portfolio: 'johndoe.dev',
    summary: 'A passionate developer with experience in building web applications.',
    experience: 'Software Engineer\nTech Corp\nDeveloped features for a major web application.',
    expYear: '2020–Present',
    projects: 'E-commerce Platform\nReact, Firebase\nBuilt a full-stack e-commerce site with payment integration.',
    skills: 'JavaScript, React, Node.js, Python, Tailwind CSS',
    education: 'B.Sc. Computer Science',
    eduYear: '2020',
    university: 'State University'
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleDownload = () => {
    window.print();
  };

  return (
    <main className="pt-[140px] pb-20 bg-[#f8fafc] dark:bg-slate-950 min-h-screen">
      <style>
        {`
          @media print {
            header, footer, .no-print, .input-section { display: none !important; }
            main { padding-top: 0 !important; background: white !important; }
            .resume-container { box-shadow: none !important; border: none !important; margin: 0 !important; width: 100% !important; }
          }
        `}
      </style>

      <div className="max-w-[1400px] mx-auto px-5">
        <div className="text-center mb-12 no-print">
          <h1 className="text-4xl md:text-5xl font-black text-slate-900 dark:text-white mb-3">Professional Resume Builder</h1>
          <p className="text-slate-500 dark:text-slate-400 font-medium">Specify your details below to see the magic.</p>
        </div>

        {/* Grid Container with aligned heights */}
        <div className="grid grid-cols-1 lg:grid-cols-[480px_1fr] gap-8 items-stretch">
          
          {/* LEFT SIDE - INPUT FORM (Height matched with preview) */}
          <div className="bg-white dark:bg-slate-900 p-8 rounded-[32px] border border-slate-100 dark:border-slate-800 shadow-sm space-y-8 input-section no-print flex flex-col justify-between h-full min-h-[1050px]">
            <div className="space-y-8">
              {/* Personal Info */}
              <section>
                <h3 className="text-sm font-black text-primary uppercase tracking-widest mb-4 flex items-center gap-2">
                  <span>01</span> Personal Details
                </h3>
                <div className="space-y-3">
                  <input type="text" name="fullName" placeholder="Full Name" value={formData.fullName} onChange={handleChange} className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 rounded-xl py-3 px-4 outline-none focus:border-primary font-bold text-slate-700 dark:text-white" />
                  <div className="grid grid-cols-2 gap-3">
                    <input type="email" name="email" placeholder="Email Address" value={formData.email} onChange={handleChange} className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 rounded-xl py-3 px-4 outline-none focus:border-primary font-bold text-xs" />
                    <input type="text" name="phone" placeholder="Phone Number" value={formData.phone} onChange={handleChange} className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 rounded-xl py-3 px-4 outline-none focus:border-primary font-bold text-xs" />
                  </div>
                </div>
              </section>

              {/* Social Links */}
              <section>
                <h3 className="text-sm font-black text-primary uppercase tracking-widest mb-4 flex items-center gap-2">
                  <span>02</span> Social & Links
                </h3>
                <div className="space-y-3">
                  <input type="text" name="linkedin" placeholder="LinkedIn Profile" value={formData.linkedin} onChange={handleChange} className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 rounded-xl py-3 px-4 outline-none focus:border-primary font-bold text-xs" />
                  <input type="text" name="github" placeholder="GitHub Link" value={formData.github} onChange={handleChange} className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 rounded-xl py-3 px-4 outline-none focus:border-primary font-bold text-xs" />
                  <input type="text" name="portfolio" placeholder="Portfolio Link" value={formData.portfolio} onChange={handleChange} className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 rounded-xl py-3 px-4 outline-none focus:border-primary font-bold text-xs" />
                </div>
              </section>

              {/* Summary */}
              <section>
                <h3 className="text-sm font-black text-primary uppercase tracking-widest mb-4"><span>03</span> Professional Summary</h3>
                <textarea name="summary" placeholder="Briefly describe your profile..." rows="2" value={formData.summary} onChange={handleChange} className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 rounded-xl py-3 px-4 outline-none focus:border-primary font-medium text-sm" />
              </section>

              {/* Experience */}
              <section>
                <h3 className="text-sm font-black text-primary uppercase tracking-widest mb-4"><span>04</span> Work Experience</h3>
                <div className="space-y-3">
                  <textarea name="experience" placeholder="Job Title \n Company Name \n Description" rows="3" value={formData.experience} onChange={handleChange} className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 rounded-xl py-3 px-4 outline-none focus:border-primary font-medium text-sm" />
                  <input type="text" name="expYear" placeholder="Duration (e.g. 2021 - Present)" value={formData.expYear} onChange={handleChange} className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 rounded-xl py-3 px-4 outline-none focus:border-primary font-bold text-xs" />
                </div>
              </section>

              {/* Projects */}
              <section>
                <h3 className="text-sm font-black text-primary uppercase tracking-widest mb-4"><span>05</span> Key Projects</h3>
                <textarea name="projects" placeholder="Project Name \n Tech \n Description" rows="3" value={formData.projects} onChange={handleChange} className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 rounded-xl py-3 px-4 outline-none focus:border-primary font-medium text-sm" />
              </section>

              {/* Skills */}
              <section>
                <h3 className="text-sm font-black text-primary uppercase tracking-widest mb-4"><span>06</span> Technical Skills</h3>
                <input type="text" name="skills" placeholder="React, Node.js, Python..." value={formData.skills} onChange={handleChange} className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 rounded-xl py-3 px-4 outline-none focus:border-primary font-bold text-sm" />
              </section>

              {/* Education */}
              <section>
                <h3 className="text-sm font-black text-primary uppercase tracking-widest mb-4"><span>07</span> Education</h3>
                <div className="space-y-3">
                  <input type="text" name="education" placeholder="Degree Name" value={formData.education} onChange={handleChange} className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 rounded-xl py-3 px-4 outline-none focus:border-primary font-bold text-sm" />
                  <input type="text" name="university" placeholder="University" value={formData.university} onChange={handleChange} className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 rounded-xl py-3 px-4 outline-none focus:border-primary font-bold text-sm" />
                </div>
              </section>
            </div>

            <button onClick={handleDownload} className="w-full py-5 bg-[#0f172a] dark:bg-white text-white dark:text-slate-900 rounded-2xl font-black text-lg shadow-xl active:scale-95 transition-all mt-8">
              Download PDF Resume 📥
            </button>
          </div>

          {/* RIGHT SIDE - LIVE PREVIEW (Minimum height 1050px) */}
          <div className="resume-container bg-white rounded-lg shadow-2xl p-10 md:p-16 min-h-[1050px] border border-slate-100 flex flex-col mx-auto w-full max-w-[850px] h-full">
            <div className="text-center mb-8 border-b-2 border-slate-900 pb-8">
              <h1 className="text-5xl font-serif font-medium text-slate-900 tracking-[4px] uppercase mb-4">{formData.fullName}</h1>
              <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-[13px] font-medium text-slate-600">
                <span>{formData.email}</span> • <span>{formData.phone}</span>
                {formData.linkedin && <>• <span className="text-slate-900">LinkedIn: {formData.linkedin}</span></>}
                {formData.github && <>• <span className="text-slate-900">GitHub: {formData.github}</span></>}
              </div>
            </div>

            <div className="space-y-8 flex-grow">
              <section>
                <h2 className="text-[11px] font-black text-slate-900 uppercase tracking-[2px] border-b border-slate-200 pb-1.5 mb-4">Summary</h2>
                <p className="text-[14px] text-slate-700 font-medium leading-relaxed">{formData.summary}</p>
              </section>

              <section>
                <h2 className="text-[11px] font-black text-slate-900 uppercase tracking-[2px] border-b border-slate-200 pb-1.5 mb-5">Experience</h2>
                <div className="flex justify-between items-start mb-2">
                  <div className="text-[14px] font-bold text-slate-900">
                    {formData.experience.split('\n')[0]} <br/>
                    <span className="font-serif italic text-slate-600">{formData.experience.split('\n')[1]}</span>
                  </div>
                  <span className="text-[13px] font-bold text-slate-500 italic">{formData.expYear}</span>
                </div>
                <ul className="list-disc ml-4 space-y-1">
                  {formData.experience.split('\n').slice(2).map((line, i) => (
                    <li key={i} className="text-[13px] text-slate-700 leading-relaxed">{line}</li>
                  ))}
                </ul>
              </section>

              <section>
                <h2 className="text-[11px] font-black text-slate-900 uppercase tracking-[2px] border-b border-slate-200 pb-1.5 mb-5">Projects</h2>
                <div className="mb-2">
                  <p className="text-[14px] font-bold text-slate-900">{formData.projects.split('\n')[0]}</p>
                  <p className="text-[13px] font-serif italic text-slate-600 mb-2">{formData.projects.split('\n')[1]}</p>
                  <p className="text-[13px] text-slate-700 leading-relaxed">{formData.projects.split('\n').slice(2).join(' ')}</p>
                </div>
              </section>

              <section>
                <h2 className="text-[11px] font-black text-slate-900 uppercase tracking-[2px] border-b border-slate-200 pb-1.5 mb-4">Skills</h2>
                <p className="text-[14px] text-slate-700 font-medium">{formData.skills}</p>
              </section>

              <section>
                <h2 className="text-[11px] font-black text-slate-900 uppercase tracking-[2px] border-b border-slate-200 pb-1.5 mb-5">Education</h2>
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-[14px] font-bold text-slate-900">{formData.education}</p>
                    <p className="text-[13px] font-serif italic text-slate-600">{formData.university}</p>
                  </div>
                  <span className="text-[13px] font-bold text-slate-500 italic">{formData.eduYear}</span>
                </div>
              </section>
            </div>
          </div>

        </div>
      </div>
    </main>
  );
};

export default ResumeBuilder;